import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import {
  Box,
  Button,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material"
import ReceiptIcon from "@mui/icons-material/Receipt"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CancelIcon from "@mui/icons-material/Cancel"
import StarIcon from "@mui/icons-material/Star"

import InvoiceCompanyDialog from "./InvoiceCompanyDialog"
import {
  fetchInvoiceCompanies,
  createInvoiceCompany as createInvoiceCompanyAction,
  updateInvoiceCompany as updateInvoiceCompanyAction,
  deleteInvoiceCompany as deleteInvoiceCompanyAction,
  testInvoiceCompanyAade,
  testInvoiceCompanyEpsilon,
  clearInvoiceCompanyError,
} from "../../store/invoiceCompanies/actions"
import {
  selectInvoiceCompanies,
  selectInvoiceCompaniesLoading,
  selectInvoiceCompaniesSaving,
  selectInvoiceCompaniesTesting,
  selectInvoiceCompaniesTestResult,
} from "../../store/invoiceCompanies/reducer"

const InvoiceCompaniesTab = ({ onSnackbar }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const companies = useSelector(selectInvoiceCompanies)
  const loading = useSelector(selectInvoiceCompaniesLoading)
  const saving = useSelector(selectInvoiceCompaniesSaving)
  const testing = useSelector(selectInvoiceCompaniesTesting)
  const testResult = useSelector(selectInvoiceCompaniesTestResult)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCompany, setEditingCompany] = useState(null)

  useEffect(() => {
    dispatch(fetchInvoiceCompanies())
  }, [dispatch])

  const handleOpenDialog = (company = null) => {
    setEditingCompany(company)
    dispatch(clearInvoiceCompanyError())
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingCompany(null)
    dispatch(clearInvoiceCompanyError())
  }

  const handleSave = async (formData) => {
    try {
      if (editingCompany) {
        dispatch(updateInvoiceCompanyAction(editingCompany.id, formData))
      } else {
        dispatch(createInvoiceCompanyAction(formData))
      }
      handleCloseDialog()
      onSnackbar({
        open: true,
        message: editingCompany ? t("Invoice company updated") : t("Invoice company created"),
        severity: "success",
      })
    } catch (error) {
      onSnackbar({
        open: true,
        message: error.message || t("Error saving invoice company"),
        severity: "error",
      })
    }
  }

  const handleDelete = async (company) => {
    if (!window.confirm(t("Are you sure you want to delete this invoice company?"))) {
      return
    }
    try {
      dispatch(deleteInvoiceCompanyAction(company.id, true))
      onSnackbar({
        open: true,
        message: t("Invoice company deleted"),
        severity: "success",
      })
    } catch (error) {
      onSnackbar({
        open: true,
        message: error.message || t("Error deleting invoice company"),
        severity: "error",
      })
    }
  }

  const handleTestAade = (id) => {
    dispatch(testInvoiceCompanyAade(id))
  }

  const handleTestEpsilon = (id) => {
    dispatch(testInvoiceCompanyEpsilon(id))
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 40, height: 40, bgcolor: "#e0e7ff", color: "#4f46e5" }}>
            <ReceiptIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="#111827">
              {t("Invoice Companies")}
            </Typography>
            <Typography variant="body2" color="#6b7280">
              {t("Manage invoice sub-companies and integrations")}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            borderRadius: 2,
            bgcolor: "#3b82f6",
            "&:hover": { bgcolor: "#2563eb" },
          }}
        >
          {t("Add Company")}
        </Button>
      </Stack>

      {/* Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f8fafc" }}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#475569",
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                }}
              >
                {t("Name")}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#475569",
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                }}
              >
                {t("VAT")}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#475569",
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                }}
              >
                {t("Refcode")}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#475569",
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                }}
              >
                {t("AADE")}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#475569",
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                }}
              >
                {t("Epsilon")}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#475569",
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                }}
              >
                {t("Status")}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#475569",
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  textAlign: "right",
                }}
              >
                {t("Actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: "#6b7280" }}>
                  {t("No invoice companies found. Add one to get started.")}
                </TableCell>
              </TableRow>
            ) : (
              companies.map((company) => (
                <TableRow
                  key={company.id}
                  hover
                  sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f8fafc" } }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: company.button_color || "#3b82f6",
                        }}
                      />
                      <Typography variant="body2" fontWeight={500}>
                        {company.name}
                      </Typography>
                      {company.is_default && (
                        <Tooltip title={t("Default Company")}>
                          <StarIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                        </Tooltip>
                      )}
                    </Stack>
                    {company.short_name && (
                      <Typography variant="caption" color="#6b7280">
                        {company.short_name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{company.vat_number}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={company.refcode}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: 1,
                        bgcolor: company.applies_vat ? "#dcfce7" : "#fef3c7",
                        borderColor: company.applies_vat ? "#16a34a" : "#d97706",
                        color: company.applies_vat ? "#16a34a" : "#d97706",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {company.etimologiera_enabled ? (
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                        label={t("Enabled")}
                        size="small"
                        sx={{
                          borderRadius: 1,
                          bgcolor: "#dcfce7",
                          color: "#16a34a",
                          "& .MuiChip-icon": { color: "#16a34a" },
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<CancelIcon sx={{ fontSize: 14 }} />}
                        label={t("Disabled")}
                        size="small"
                        sx={{
                          borderRadius: 1,
                          bgcolor: "#f1f5f9",
                          color: "#64748b",
                          "& .MuiChip-icon": { color: "#64748b" },
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {company.epsilon_enabled ? (
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                        label={t("Enabled")}
                        size="small"
                        sx={{
                          borderRadius: 1,
                          bgcolor: "#dcfce7",
                          color: "#16a34a",
                          "& .MuiChip-icon": { color: "#16a34a" },
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<CancelIcon sx={{ fontSize: 14 }} />}
                        label={t("Disabled")}
                        size="small"
                        sx={{
                          borderRadius: 1,
                          bgcolor: "#f1f5f9",
                          color: "#64748b",
                          "& .MuiChip-icon": { color: "#64748b" },
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={company.is_active ? t("Active") : t("Inactive")}
                      size="small"
                      sx={{
                        borderRadius: 1,
                        bgcolor: company.is_active ? "#dcfce7" : "#fee2e2",
                        color: company.is_active ? "#16a34a" : "#dc2626",
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={t("Edit")}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenDialog(company)
                          }}
                        >
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("Delete")}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDelete(company)
                          }}
                          sx={{ color: "#dc2626" }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog */}
      <InvoiceCompanyDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        company={editingCompany}
        onSave={handleSave}
        saving={saving}
        onTestAade={handleTestAade}
        onTestEpsilon={handleTestEpsilon}
        testing={testing}
        testResult={testResult}
      />
    </Box>
  )
}

export default InvoiceCompaniesTab
